import React, {useContext, useEffect} from "react"
import Row from "@amzn/meridian/row";
import Tag from "@amzn/meridian/tag";
import Column from "@amzn/meridian/column"
import ControlGroup from "@amzn/meridian/control-group"
import Checkbox from "@amzn/meridian/checkbox"
import {LineHaulFilterContext} from "./context/LineHaulFilterContext";
import Select, { SelectOption } from "@amzn/meridian/select"
import Text from "@amzn/meridian/text"
import {FormattedMessage, injectIntl} from "react-intl";
import {ConfigContext} from "../global/context/ConfigContext";
import {getFeaturesFromParent, getNodeCapabilityFromParent} from "../handler/ParentDataHandler";
import {chainWalk, getDisplayName, parseCapabilitiesResponse} from "../util/Util";
import {ACTION_TYPE, CAPABILITY, FEATURES} from "../util/Constants";
import Link from "@amzn/meridian/link";
import Box from "@amzn/meridian/box";
import {MobileDisplay} from "../common/MobileDisplay";
import {useDeviceLayout} from "../common/context/DeviceLayoutContext";
import FilterExpander from "../common/FilterExpander";

const LineHaulFilter = ({ intl }) => {
    const { filter, dispatch } = useContext(LineHaulFilterContext)
    const {isMobile} = useDeviceLayout();
    const isPackageSizeFilterVisible = filter.Split.includes('PackageSize')
    const isEADFilterVisible = filter.Split.includes('EAD')
    const isCycleFilterVisible = filter.Split.includes('Cycle')
    const isCPTFilterVisible = filter.Split.includes('CPT')
    const isClusterFilterVisible = filter.Split.includes('Cluster')
    const isOtherAttributesVisible = filter.Split.includes('OtherAttributes')
    const formattedIdPrefix = "linehaul_filter."

    const getCycleName = (cycleId) => {
        if(cycleId === "NONE") {
            return getDisplayName("Unplanned", formattedIdPrefix);
        }
        return cycleId;
    }

    const getCPTLabel = (label) => {
        if(label === "NONE") {
            return getDisplayName("Unplanned", formattedIdPrefix);
        }
        return label;
    }

    const {configDispatch, configViewModel} = useContext(ConfigContext);

    useEffect(() => {
        getNodeCapabilityFromParent().then(capabilityResponse => {
            const capability = parseCapabilitiesResponse(capabilityResponse)
            configDispatch({type: 'GET_CAPABILITIES', capability});
            getFeaturesFromParent([FEATURES.IS_CPT_FILTER_ENABLED]).then(featureStatus => {
                configDispatch({type: ACTION_TYPE.GET_FEATURES_STATUS, featureStatus: featureStatus.data});
                dispatch({type: 'LOAD_VIEW', data:{dispatcher: dispatch}});
                if (isCPTFilterEnabled) {
                    dispatch({type: 'LOAD_CPT_VIEW', data:{dispatcher: dispatch}});
                } else {
                    dispatch({type: 'LOAD_CYCLE_VIEW', data:{dispatcher: dispatch}});
                }
            })
        })
    }, []);

    const isCPTFilterEnabled = chainWalk(() => configViewModel.featureStatus[FEATURES.IS_CPT_FILTER_ENABLED], false);

    const renderCycleCheckBox = () => {
        return CAPABILITY.AR_NEXT_GEN !== configViewModel.capability && (
            <Checkbox value='Cycle' disabled={!filter.Filters.hasOwnProperty("Cycle")}>
                <FormattedMessage id="linehaul_filter.cycle" defaultMessage="Cycle"/>
            </Checkbox>
        )
    }

    const renderCPTCheckBox = () => {
        const filterLabel= "CPT."+filter.CPTLabel
        return CAPABILITY.AR_NEXT_GEN !== configViewModel.capability && (
            <Checkbox value='CPT' disabled={!filter.Filters.hasOwnProperty("CPT")}>
                <FormattedMessage id={filterLabel} defaultMessage="CPT"/>
            </Checkbox>
        )
    }

    const renderClusterCheckBox = () => {
        return CAPABILITY.AR_NEXT_GEN !== configViewModel.capability && (
            <Checkbox value='Cluster' disabled={!filter.Filters.hasOwnProperty("Cluster")}>
                <FormattedMessage id="linehaul_filter.cluster" defaultMessage="Cluster"/>
            </Checkbox>
        )
    }

    const isFilterSet = () => {
        let result = false;
        Object.entries(filter).forEach(entry => {
            const key = entry[0]
            const values = entry[1]
            result = result || (Array.isArray(values) && values.length && key !== 'Split');
        });
        return result;
    }

    const clearAllFilters = () => {
        dispatch({type: 'REMOVE_ALL_FILTER'});
    }

    const renderFilterOptions = () => {
        return (
            <React.Fragment>
                <Column  heights="fill" alignmentHorizontal="center">
                    <ControlGroup
                        label="Split"
                        name="split"
                        onChange={(data) => dispatch({ type: 'SELECT_SPLIT', Split:data })}
                        value = {filter.Split}>
                        <Checkbox value='PackageSize' disabled={!filter.Filters.hasOwnProperty("PackageSize")}>
                            <FormattedMessage id="linehaul_filter.packagesize" defaultMessage="Package Size"/>
                        </Checkbox>
                        <Checkbox value='EAD' disabled={!filter.Filters.hasOwnProperty("EAD")}>
                            <FormattedMessage id="linehaul_filter.ead" defaultMessage="Station EAD"/>
                        </Checkbox>
                        {isCPTFilterEnabled ? renderCPTCheckBox() : renderCycleCheckBox()}
                        {renderClusterCheckBox()}
                        <Checkbox value='OtherAttributes' disabled={!filter.Filters.hasOwnProperty("OtherAttributes")}>
                            <FormattedMessage id="linehaul_filter.otherattributes" defaultMessage="Other Attributes"/>
                        </Checkbox>
                    </ControlGroup>
                </Column>
                {isPackageSizeFilterVisible ? <Column  heights="fill" alignmentHorizontal="center" spacingInset="none medium none">
                    <ControlGroup
                        label={intl.formatMessage({id: 'linehaul_filter.packagesize'})}
                        name="packageSize"
                        onChange={(data) => dispatch({ type: 'SELECT_PACKAGE_SIZE', PackageSize:data })}
                        value = {filter.PackageSize}>
                        {filter.Filters.PackageSize.map(packageSize => (
                            <Checkbox key={packageSize} value={packageSize}>{packageSize}</Checkbox>
                        ))}
                    </ControlGroup>
                </Column> : null}
                {isEADFilterVisible ? <Column  heights="fill" alignmentHorizontal="left" spacingInset="none medium none">
                    <Text type="h100" alignment="left">
                        <FormattedMessage id="linehaul_filter.ead" defaultMessage="Station EAD"/>
                    </Text>
                    <Select
                        onChange={(data) => dispatch({ type: 'SELECT_EAD', EAD:data })}
                        value = {filter.EAD}
                        size="small"
                        placeholder="Station EAD"
                        width={150}>
                        {filter.Filters.EAD.map(ead => (
                            <SelectOption key={ead} label={ead} value={ead}/>
                        ))}
                    </Select>
                </Column> : null}
                {(isCycleFilterVisible && !isCPTFilterEnabled)? <Column  heights="fill" alignmentHorizontal="center" spacingInset="none medium none">
                    <ControlGroup
                        label={intl.formatMessage({id: 'linehaul_filter.cycle'})}
                        name="cycles"
                        onChange={(data) => dispatch({ type: 'SELECT_CYCLE', Cycle:data })}
                        value = {filter.Cycle}>
                        {filter.Filters.Cycle.map(cycle => (
                            <Checkbox key={cycle.cycleId} value={cycle.cycleId}>{getCycleName(cycle.cycleId)}</Checkbox>
                        ))}
                    </ControlGroup>
                </Column> : null}

                {(isCPTFilterVisible && isCPTFilterEnabled) ? <Column  heights="fill" alignmentHorizontal="center" spacingInset="none medium none">
                    <ControlGroup
                        label={intl.formatMessage({id: 'CPT.'+filter.CPTLabel})}
                        name="CPT"
                        onChange={(data) => dispatch({ type: 'SELECT_CPT', CPT:data })}
                        value = {filter.CPT}>
                        {filter.Filters.CPT.map(CPT => (
                            <Checkbox key={CPT.label} value={CPT.label}>{getCPTLabel(CPT.label)}</Checkbox>
                        ))}
                    </ControlGroup>
                </Column> : null}

                {isClusterFilterVisible ? <Column  heights="fill" alignmentHorizontal="left" spacingInset="none medium none">
                    <Text type="h100" alignment="left">
                        <FormattedMessage id="linehaul_filter.cluster" defaultMessage="Cluster"/>
                    </Text>
                    <Select
                        onChange={(data) => dispatch({ type: 'SELECT_CLUSTER', Cluster:data })}
                        value = {filter.Cluster}
                        size="small"
                        placeholder="Cluster"
                        width={150}>
                        {filter.Filters.Cluster.sort().map(cluster => (
                            <SelectOption key={cluster} label={cluster} value={cluster}/>
                        ))}
                    </Select>
                </Column> : null}
                {isOtherAttributesVisible ? <Column  heights="fill" alignmentHorizontal="center" spacingInset="none medium none">
                    <ControlGroup
                        label={intl.formatMessage({id: 'linehaul_filter.otherattributes'})}
                        name="other_attributes"
                        onChange={(data) => dispatch({ type: 'SELECT_OTHER_ATTRIBUTES', OtherAttributes:data })}
                        value = {filter.OtherAttributes}>
                        {filter.Filters.OtherAttributes.map(otherAttributes => (
                            <Checkbox key={otherAttributes} value={otherAttributes}>{getDisplayName(otherAttributes, formattedIdPrefix)}</Checkbox>
                        ))}
                    </ControlGroup>
                </Column> : null}
            </React.Fragment>
        )
    }

    return (
        <Box>
            {isMobile ?
                <FilterExpander>
                    <Column spacingInset="medium large" backgroundColor="secondary">
                        <Column width="100%" alignmentVertical="top" alignmentHorizontal={"left"} >
                            {renderFilterOptions()}
                        </Column>
                    </Column>
                </FilterExpander> :
                <Column spacingInset="medium large" backgroundColor="secondary">
                    <Row width="100%" alignmentVertical="top">
                        {renderFilterOptions()}
                    </Row>
                </Column>
            }
            <MobileDisplay visibility={false}>
                <Row width="100%">
                    <div className="filter-tag">
                        {Object.entries(filter).map(entry => {
                            const key = entry[0]
                            const values = entry[1]
                            return (
                                (Array.isArray(values) && key !== 'Split')? values.map(value => {
                                    return <Tag key = {value} type="theme"
                                                onClose={() => dispatch({ type: 'REMOVE_FILTER', data: {key, value}})}>{value}</Tag>
                                })  : null
                            )
                        })}
                    </div>
                    {isFilterSet() ? <Link onClick={clearAllFilters} type="secondary">
                        <FormattedMessage id="linehaul_filter.clear_all" defaultMessage="Clear all"/>
                    </Link> : null}
                </Row>
            </MobileDisplay>
        </Box>
    );
}

export default injectIntl(LineHaulFilter)


// WEBPACK FOOTER //
// ./src/components/linehaul/LineHaulFilter.js